import * as React from "react";
import { styled, Tooltip } from "@mui/material";
import { ValueWithSource } from "@kahunmedical/common-configuration";
import { useEffect, useState } from "react";
import { ReactComponent as EditIcon } from "../../assets/editSettingsIcon.svg";
import { ConfigSettingsEnvironments } from "../common/ConfigSettingsKeyPerNodeEnvironment";
import clsx from "clsx";
import { CLINIC_SOURCE_IDENTIFIER } from "../common/useConfigurationSettingsManager";
import { MASTER_CLINIC_NAME } from "fe-common/commonTypes/masterClinicDefaults";

//region [[ Styles ]]

const SettingSourceIconView = styled("div")({});

const SourceBadge = styled("div")({
  background: "#E6E4FF",
  color: "#4247C9",
  borderRadius: "6px",
  display: "flex",
  flexFlow: "row",
  justifyContent: "center",
  alignItems: "center",
  margin: "0 8px",
  padding: "5px 12px",
  fontSize: "9px",
  fontWeight: 600,
  "& svg": {
    fill: "#4247C9",
  },
  "&.icon": {
    width: "22px",
    height: "22px",
    padding: 0,
  },
  "&.dev": {
    color: "#5C5C5D",
    background: "#DCD9E3",
  },
  "&.prod": {
    color: "#008A37",
    background: "#D6EBDE",
  },
  "&.default": {
    color: "#348a64",
    background: "#D6EBDE",
  },
  "&.clinic": {
    color: "#232323",
    background: "#FFE7B1",
  },
  "&.local": {
    color: "#4247C9",
    background: "#E6E4FF",
  },
  "&.master": {
    color: "#FFFFFF",
    background: "#4247C9",
  },
});
//endregion [[ Styles ]]

//region [[ Props ]]

export interface SettingSourceIconProps {
  valueWithSource: ValueWithSource;
}

//endregion [[ Props ]]

//region [[ Functions ]]
const getSource = (valueWithSource: ValueWithSource) => {
  if (!valueWithSource) {
    return ValueSource.NotSet;
  }
  if (valueWithSource?.value !== null && !valueWithSource.source) {
    return ValueSource.LocalEdit;
  }
  if (
    valueWithSource.source &&
    (valueWithSource.source === "dev" || valueWithSource.source === "prod")
  ) {
    return ValueSource.EnvDefault;
  }
  if (valueWithSource.source && valueWithSource.source === "default") {
    return ValueSource.Default;
  }
  return ValueSource.CustomSource;
};
//endregion [[ Functions ]]
enum ValueSource {
  LocalEdit = "Local Edit - Pending save",
  EnvDefault = "Environment Default",
  Default = "Default value",
  NotSet = "Not Set",
  CustomSource = "Override:",
}

export const SettingSourceIcon = ({
  valueWithSource,
  ...props
}: SettingSourceIconProps) => {
  const [valueSource, setValueSource] = useState<ValueSource>(
    ValueSource.NotSet,
  );

  useEffect(() => {
    setValueSource(getSource(valueWithSource));
  }, [valueWithSource]);

  return (
    <Tooltip
      title={
        valueSource +
        (valueWithSource?.source ? ` - ${valueWithSource?.source}` : "")
      }
    >
      <SettingSourceIconView className={"setting-source-icon"}>
        {valueSource === ValueSource.LocalEdit && (
          <SourceBadge className={"icon"}>
            <EditIcon />
          </SourceBadge>
        )}
        {valueSource === ValueSource.CustomSource && (
          <SourceBadge
            className={clsx(
              valueWithSource?.source === CLINIC_SOURCE_IDENTIFIER
                ? "clinic"
                : valueWithSource?.source === MASTER_CLINIC_NAME
                ? "master"
                : "local",
            )}
          >
            {valueWithSource?.source}
          </SourceBadge>
        )}
        {valueSource === ValueSource.EnvDefault &&
          valueWithSource?.source ===
            ConfigSettingsEnvironments.Development && (
            <SourceBadge className={"dev"}>
              {valueWithSource?.source}
            </SourceBadge>
          )}
        {valueSource === ValueSource.EnvDefault &&
          valueWithSource?.source === ConfigSettingsEnvironments.Production && (
            <SourceBadge className={"prod"}>
              {valueWithSource?.source}
            </SourceBadge>
          )}
      </SettingSourceIconView>
    </Tooltip>
  );
};
